import React, { useState, useEffect } from 'react'

import clsx from 'clsx'

import {
    useTheme,
    makeStyles,
    Theme,
    Typography,
    Button,
    IconButton,
} from '@material-ui/core'
import { FilterList as FilterIcon } from '@material-ui/icons'
import { Send as SendIcon } from '@material-ui/icons'
import { TableChart as TableIcon } from '@material-ui/icons'
import { Timeline as ChartIcon } from '@material-ui/icons'
import { GetApp as DownloadIcon } from '@material-ui/icons'

import { getDefaultSummaryData, useBilling, ValidSortField } from '../../hooks'
import { InvoiceTable } from '../InvoiceTable/InvoiceTable'
import { InternalInvoice } from '../../models'
import { GLOBAL_TOP_BAR_HEIGHT } from '../../constants'
import { InternalInvoiceModal } from '../InternalInvoice'
import { FilterDrawer } from './components/FilterDrawer'
import { InvoiceSummaryStats } from './components/InvoiceSummaryStats'
import { EzosLoader } from '../EzosLoader/EzosLoader'
import { PaymentTrendsChart } from './components/dashboard/PaymentTrendsChart'
import { useBillingGroups } from '../../hooks/useBillingGroups'
import { FilterChips } from './FilterChips'
import { Container } from '../Container'
import { toast } from 'react-toastify'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        height: `calc(100vh - ${GLOBAL_TOP_BAR_HEIGHT}px)`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    contentShift: {
        marginRight: 320,
    },
    header: {
        flex: '0 0 auto',
        padding: theme.spacing(2),
        marginTop: theme.spacing(2),
        margin: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        minHeight: 50,
        maxHeight: 50,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
    },
    title: {
        flex: 1,
        justifyContent: 'center',
    },
    headerButtons: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    summaryContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 100,
    },
    tableContainer: {
        flex: '1 1 auto',
        overflow: 'hidden',
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
    },
    loaderContainer: {
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    filterChips: {
        marginTop: theme.spacing(1),
        flex: 1,
    },
}))

interface InvoiceListProps {
    propertyId?: number
    hideSendButton?: boolean
    hideSelectionColumn?: boolean
    visibleColumns?: (keyof InternalInvoice)[]
    onSendInvoices?: () => void
    onMarkReviewed?: () => void
    selectedInvoices?: string[]
    setSelectedInvoices?: (invoiceIds: string[]) => void
    selectAllRows?: boolean
    onSelectAll?: (event: React.ChangeEvent<HTMLInputElement>) => void
    onSelectOne?: (id: string) => void
    onSelectAllPages?: () => void
    allPagesSelected?: boolean
    isDashboardOpen?: boolean
    onToggleDashboard?: () => void
    isAdmin?: boolean
    onCurrentPageInvoicesChange?: (invoiceIds: string[]) => void
    onTotalCountChange?: (totalCount: number) => void
}

export const InvoiceList: React.FC<InvoiceListProps> = ({
    propertyId,
    hideSendButton = false,
    hideSelectionColumn = false,
    visibleColumns = [
        'invoice_number',
        'billing_group_display_name',
        'billing_email',
        'status',
        'issue_date',
        'due_date',
        'total_amount',
        'balance_due',
    ],
    onSendInvoices = () => {},
    onMarkReviewed = () => {},
    selectedInvoices = [],
    setSelectedInvoices = () => {},
    selectAllRows = false,
    onSelectAll = () => {},
    onSelectOne = () => {},
    onSelectAllPages = () => {},
    allPagesSelected = false,
    isDashboardOpen = false,
    onToggleDashboard = () => {},
    isAdmin = false,
    onCurrentPageInvoicesChange = () => {},
    onTotalCountChange = () => {},
}) => {
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(50)
    const [sortField, setSortField] = useState<ValidSortField>('invoice_number')
    const [order, setOrder] = useState<'asc' | 'desc'>('asc')
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(true)
    const [isChartView, setIsChartView] = useState(false)
    const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(
        null,
    )
    const [reviewLoading, setReviewLoading] = useState(false)
    const [isExportingCsv, setIsExportingCsv] = useState(false)

    const classes = useStyles()
    const theme = useTheme()
    const { billingGroups } = useBillingGroups()

    // Hook handles the data fetching
    const {
        data,
        isLoading,
        refetch,
        recordPayment,
        isRecordPaymentLoading,
        recordPaymentError,
        paymentTrends,
        isPaymentTrendsLoading,
        refetchPaymentTrends,
        voidInvoice,
        voidPayment,
        markReviewed,
        summaryData,
        isSummaryLoading,
        refetchSummary,
        filters,
        isApplyingFilters,
        handleFiltersChange,
        handleApplyFilters,
        handleResetFilters,
        billableOrgs,
        exportCsv,
        updateInvoiceDates,
        addLineItem,
        isAddLineItemLoading,
        addLineItemError,
        editLineItem,
        isEditLineItemLoading,
        editLineItemError,
        removeLineItem,
        isRemoveLineItemLoading,
        removeLineItemError,
        // refetchSyncQuickbooks,
    } = useBilling({
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        sort: order === 'desc' ? `-${sortField}` : sortField,
        ...(isAdmin ? { all_properties: true } : { property_id: propertyId }),
        isAdmin,
    })

    const handleExportCsv = async () => {
        setIsExportingCsv(true)
        try {
            await exportCsv()
            toast.success('CSV exported successfully')
        } catch (error) {
            console.error('Error exporting CSV:', error)
            toast.error('Failed to export CSV')
        } finally {
            setIsExportingCsv(false)
        }
    }

    const handleRequestSort = (
        property: keyof InternalInvoice,
        newOrder: 'asc' | 'desc',
    ) => {
        setSortField(property as ValidSortField)
        setOrder(newOrder)
        // refetch()
    }

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        const urlPage = parseInt(params.get('page') || '0')
        const urlPageSize = parseInt(params.get('pageSize') || '50')
        setPage(urlPage)
        setRowsPerPage(urlPageSize)
    }, [])

    // Notify parent of current page invoice IDs when data changes
    useEffect(() => {
        if (data?.data.results) {
            const currentPageInvoiceIds = data.data.results.map(
                (invoice) => invoice.id,
            )
            onCurrentPageInvoicesChange(currentPageInvoiceIds)
        }
    }, [data?.data.results, onCurrentPageInvoicesChange])

    // Notify parent of total count when data changes
    useEffect(() => {
        if (data?.data.count !== undefined) {
            onTotalCountChange(data.data.count)
        }
    }, [data?.data.count, onTotalCountChange])

    const selectedInvoice =
        data?.data.results.find(
            (invoice) => invoice.id === selectedInvoiceId,
        ) || null

    // Determine which columns to show based on isAdmin
    const defaultColumns = isAdmin
        ? [
              'invoice_number',
              'billing_group_display_name',
              'billing_email',
              'status',
              'issue_date',
              'due_date',
              'total_amount',
              'balance_due',
          ]
        : [
              'invoice_number',
              'billing_group_display_name',
              'billing_email',
              'status',
              'issue_date',
              'due_date',
              'total_amount',
              'balance_due',
          ]

    const effectiveVisibleColumns = visibleColumns || defaultColumns

    return (
        <Container
            className={clsx(classes.root, {
                [classes.contentShift]: filterDrawerOpen,
            })}
        >
            {/* Fixed Header */}
            <Container className={classes.header}>
                <Container className={classes.title}>
                    <Typography variant="h5">Internal Invoices</Typography>
                </Container>
                <Container className={classes.headerButtons}>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<DownloadIcon />}
                        onClick={handleExportCsv}
                        disabled={isExportingCsv || isLoading}
                        style={{ marginRight: theme.spacing(2) }}
                    >
                        {isExportingCsv ? 'Exporting...' : 'Export CSV'}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SendIcon />}
                        onClick={() => {
                            setReviewLoading(true)
                            markReviewed({
                                invoiceIds: selectedInvoices.map((id) =>
                                    parseInt(id),
                                ),
                            })
                                .then(() => {
                                    toast.success(
                                        'Invoices marked as ready to send',
                                    )
                                    setSelectedInvoices([])
                                })
                                .finally(() => {
                                    setReviewLoading(false)
                                })
                        }}
                        disabled={
                            (selectedInvoices.length === 0 && !selectAllRows) ||
                            isLoading ||
                            reviewLoading
                        }
                    >
                        Mark Ready to Send ({selectedInvoices.length})
                    </Button>
                    <IconButton
                        size="small"
                        onClick={() => setIsChartView(!isChartView)}
                        style={{ marginLeft: theme.spacing(2) }}
                    >
                        {isChartView ? <TableIcon /> : <ChartIcon />}
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => setFilterDrawerOpen(true)}
                        style={{ marginLeft: theme.spacing(2) }}
                    >
                        <FilterIcon />
                    </IconButton>
                </Container>
            </Container>

            {/* Summary Section */}
            <Container className={classes.summaryContainer}>
                <InvoiceSummaryStats
                    summaryData={summaryData || getDefaultSummaryData()}
                    isLoading={isSummaryLoading}
                    paymentTrends={paymentTrends}
                />
            </Container>

            {/* Filter Chips */}
            <Container className={classes.filterChips}>
                <FilterChips
                    filters={filters}
                    onDelete={(filter) => {
                        const newFilters = { ...filters }
                        delete newFilters[filter]
                        handleFiltersChange(newFilters)
                        handleApplyFilters(newFilters)
                    }}
                    billingGroups={billingGroups}
                />
            </Container>

            {/* Table or Chart */}

            <Container className={classes.tableContainer}>
                {isLoading ? (
                    <div className={classes.loaderContainer}>
                        <EzosLoader />
                    </div>
                ) : isChartView ? (
                    <PaymentTrendsChart
                        data={paymentTrends || {}}
                        isLoading={isPaymentTrendsLoading}
                        onRefresh={refetchPaymentTrends}
                    />
                ) : (
                    <InvoiceTable
                        invoices={data?.data.results}
                        orderBy={sortField as keyof InternalInvoice}
                        order={order}
                        onRequestSort={handleRequestSort}
                        onViewInvoice={(invoice) => {
                            setSelectedInvoiceId(invoice.id)
                        }}
                        tableHeight="100%"
                        page={page}
                        rowsPerPage={rowsPerPage}
                        totalCount={data?.data.count || 0}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        selected={selectedInvoices}
                        onSelectAll={onSelectAll}
                        onSelectOne={onSelectOne}
                        allPagesSelected={allPagesSelected}
                        onSelectAllPages={onSelectAllPages}
                        hideSelectionColumn={!isAdmin || hideSelectionColumn}
                        visibleColumns={effectiveVisibleColumns}
                    />
                )}
            </Container>

            <FilterDrawer
                open={filterDrawerOpen}
                onClose={() => setFilterDrawerOpen(false)}
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onApply={handleApplyFilters}
                onReset={handleResetFilters}
                isLoading={isApplyingFilters}
                showCompanyFilter={isAdmin}
                organizations={billableOrgs}
                billingGroups={billingGroups}
            />

            <InternalInvoiceModal
                invoice={selectedInvoice}
                onClose={() => setSelectedInvoiceId(null)}
                onVoidInvoice={(reason) => {
                    if (!selectedInvoice) return
                    voidInvoice({
                        invoiceId: selectedInvoice.id,
                        reason,
                    })
                    setSelectedInvoiceId(null)
                }}
                onVoidPayment={(paymentId, reason) => {
                    if (!selectedInvoice) return
                    voidPayment({
                        invoiceId: selectedInvoice.id,
                        paymentId,
                        reason,
                    })
                }}
                onMarkReviewed={(invoiceId) => {
                    if (!selectedInvoice) return
                    markReviewed({
                        invoiceIds: [parseInt(invoiceId)],
                    })
                }}
                onAddPayment={recordPayment}
                viewOnly={!isAdmin || hideSendButton}
                onUpdateInvoiceDates={(invoiceId, issue_date, due_date) =>
                    updateInvoiceDates({ invoiceId, issue_date, due_date })
                }
                onAddLineItem={async (data) => {
                    await addLineItem(data)
                }}
                onEditLineItem={async (data) => {
                    await editLineItem(data)
                }}
                onRemoveLineItem={async (data) => {
                    await removeLineItem(data)
                }}
                isAddLineItemLoading={isAddLineItemLoading}
                isEditLineItemLoading={isEditLineItemLoading}
                isRemoveLineItemLoading={isRemoveLineItemLoading}
            />
        </Container>
    )
}
